type Point = [number, number];

const CX = 16;
const CY = 14.5;
const TILT = (-10 * Math.PI) / 180;
const EYES: Point[] = [
  [13, 13.4],
  [19, 13.4],
];

const f = (n: number) => Math.round(n * 100) / 100;

const tilt = ([x, y]: Point): Point => {
  const dx = x - CX;
  const dy = y - CY;

  return [
    CX + dx * Math.cos(TILT) - dy * Math.sin(TILT),
    CY + dx * Math.sin(TILT) + dy * Math.cos(TILT),
  ];
};

const pt = (p: Point) => `${f(p[0])} ${f(p[1])}`;

const pebble = () => {
  const [a, b] = [10, 10.8];
  const ks = [0.82, 0.6, 0.68, 0.78];
  const ends: Point[] = [
    [CX + a, CY],
    [CX, CY - b],
    [CX - a, CY],
    [CX, CY + b],
  ];
  const handles: [Point, Point][] = [
    [
      [CX + a, CY - ks[0] * b],
      [CX + ks[0] * a, CY - b],
    ],
    [
      [CX - ks[1] * a, CY - b],
      [CX - a, CY - ks[1] * b],
    ],
    [
      [CX - a, CY + ks[2] * b],
      [CX - ks[2] * a, CY + b],
    ],
    [
      [CX + ks[3] * a, CY + b],
      [CX + a, CY + ks[3] * b],
    ],
  ];

  return (
    `M${pt(tilt(ends[0]))}` +
    handles
      .map(
        ([h1, h2], i) =>
          `C${pt(tilt(h1))} ${pt(tilt(h2))} ${pt(tilt(ends[(i + 1) % 4]))}`,
      )
      .join("") +
    "Z"
  );
};

/** The body, tilted like a head listening. */
export const PEBBLE_PATH = pebble();

/** A pill eye on the tilted face, nudged by `gaze` in screen units and
 *  raised by `lift` on the face's own axis. */
const pill = ([x, y]: Point, [gx, gy]: Point, scale: number, lift = 0) => {
  const r = 1.2 * scale;
  const half = 2.5 * scale - r;
  const at = (dx: number, dy: number) => {
    const [tx, ty] = tilt([x + dx, y + dy - lift]);

    return pt([tx + gx, ty + gy]);
  };

  return (
    `M${at(-r, -half)}A${f(r)} ${f(r)} 0 0 1 ${at(r, -half)}` +
    `L${at(r, half)}A${f(r)} ${f(r)} 0 0 1 ${at(-r, half)}Z`
  );
};

export interface EchoEyes {
  gaze: Point;
  scale: number;
  /** Per eye (left, right), in face units: `thinking` holds them at two
   *  heights. */
  lift?: Point;
}

/** Both eyes as one path, nothing while blinking. */
export const echoEyesPath = (eyes: false | EchoEyes) =>
  eyes
    ? EYES.map((e, i) => pill(e, eyes.gaze, eyes.scale, eyes.lift?.[i])).join(
        "",
      )
    : "";

/** The body with the eyes cut out as holes (evenodd), or whole while blinking. */
export const echoMarkPath = (eyes: false | EchoEyes) =>
  PEBBLE_PATH + echoEyesPath(eyes);
